/**
 * IndexedDB-based cache class
 */
class PriceCacheDB {
  constructor(
    dbName = "ProductPriceDB",
    storeName = "ProductPrices",
    maxAgeMs = 5 * 60 * 1000
  ) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.maxAgeMs = maxAgeMs;
    this.dbPromise = this.openDB();
  }

  openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      request.onerror = () => reject("Failed to open IndexedDB");
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });
  }

  async get(key) {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const transaction = db.transaction([this.storeName]);
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        if (!result || Date.now() - result.timestamp > this.maxAgeMs) {
          resolve(null);
        } else {
          resolve(result.value);
        }
      };
      request.onerror = () => resolve(null);
    });
  }

  async set(key, value) {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      store.put({ value, timestamp: Date.now() }, key);
      transaction.oncomplete = () => resolve();
    });
  }

  async delete(key) {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      store.delete(key);
      transaction.oncomplete = () => resolve();
    });
  }

  async cleanExpired() {
    const db = await this.dbPromise;
    const transaction = db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);
    const request = store.openCursor();
    const now = Date.now();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (now - cursor.value.timestamp > this.maxAgeMs) {
          store.delete(cursor.key);
        }
        cursor.continue();
      }
    };
  }
}

/**
 * Defines a class for updating product prices in real time.
 */
class PriceUpdater {
  /**
   * Constructs an instance of the PriceUpdater class.
   * @param {string} shopHiveKey The secret key for API authentication.
   * @param {string} apiUrl The base URL for API requests.
   */
  constructor(shopHiveKey, apiUrl, options = {}) {
    this.shopHiveKey = "2c9355b233cfcf91aa91"; // TODO: set your shopHiveKey here
    this.apiUrl = "https://a1.shophive.ai"; // TODO: set apiUrl here, like "https://xxx.xxx.com"
    
    this.config = {
      enableWebSocket: false,        // 默认关闭 WebSocket
      enablePolling: true,           // 默认开启轮询
      pollingInterval: 60000,       // 轮询间隔  5分钟 (60秒)
      cacheEnabled: true,            // 启用缓存
      cacheMaxAge: 180000,          // 缓存 3分钟
      enableNotifications: true,     // 启用通知
      forceRefresh: false             // 强制刷新，绕过缓存（默认开启）
    };
    
    this.config = { ...this.config, ...options };
    
    this.intervalId = null;
    this.isB2BUser = this.getIsB2BCustomer() || false;
    this.socket = null;
    this.need_websocket = this.config.enableWebSocket;
    this.cache = new PriceCacheDB();
    this.cache.cleanExpired();
    this.isTriggeringNativeRefresh = false;
    this.init();
  }

  /**
   * Initializes the PriceUpdater based on configuration.
   */
  init() {
    console.log('this.isB2BUser',this.isB2BUser)
    if (!this.isB2BUser) {
      console.log('Non-B2B user detected, skipping price update initialization');
      return;
    }

    if (this.config.enableWebSocket) {
      this.initSocket();
    }
    
    if (this.config.enablePolling) {
      this.startPolling();
    }
    
    this.initEventListeners();
    this.initMutationObserver();
  }
  
  startPolling() {
    if (!this.config.enablePolling) return;
    this.startPriceUpdates(false);
    this.intervalId = setInterval(() => {
      this.startPriceUpdates(true);
    }, this.config.pollingInterval);
  }
 
  stopPolling() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  initEventListeners() {
    this.initPDPEventListeners();
    this.initPLPEventListeners();
    this.initCartEventListeners();
  }

  /**
   * Initialize MutationObserver to monitor DOM changes, handle dynamically loaded products
   */
  initMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdatePrices = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && (
                node.classList.contains('js-pagination-result') ||
                node.classList.contains('product-card') ||
                node.querySelector('.js-pagination-result') ||
                node.querySelector('.product-card')
              )) {
                console.log('New product cards detected, will update prices');
                shouldUpdatePrices = true;
              }
            }
          });
        }
      });
      
      // if there are new product cards, update prices after a delay
      if (shouldUpdatePrices) {
        setTimeout(() => {
          this.updatePLPPrice();
        }, 100);
      }
    });
    
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    console.log('MutationObserver initialized for dynamic product loading');
  }


  async startPriceUpdates(isIntervalRequest=false) {
    if (this.startPriceUpdatesRunning) {
      console.log('startPriceUpdates already running, skipping');
      return;
    }
    
    this.startPriceUpdatesRunning = true;
    
    try {
    if (this.isB2BUser) {
        console.log('start  b2b startPriceUpdates')
        if (this.isCartPage()) {
          /**
           * This has been commented to avoid extra API calls on cart page,
           */
          // await this.getCartProductPriceList();
          // await this.getCartSelectedWarehouse()
        } else {
          await this.getProductPrice(isIntervalRequest);
        }
    }else{
      console.log('startPriceUpdates not b2b, skipping');
    }
    } finally {
      this.startPriceUpdatesRunning = false;
    }
  }
  
  initPDPEventListeners() {
    document.addEventListener('on:variant:change', (event) => {
      this.changeItemsPrice(Number(event.detail?.qty || 1), [event?.detail.shophyveVariant]);
    });
  }

  
  initPLPEventListeners() {
    document.addEventListener('change', (e) => {
      if (e.target.matches('.facet-filters input, .facet-filters select')) {
        this.updatePLPPrice();
      }
    });
  }

  
  initCartEventListeners() {
    this.interceptCartOperations();
  }

  /**
   * Intercept cart native operations, let shophyve API execute first
   */
  interceptCartOperations() {
    const waitForCartItems = () => {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems) {
        setTimeout(waitForCartItems, 100);
        return;
      }
      
      // save original method
      const originalUpdateQuantity = cartItems.updateQuantity.bind(cartItems);
      
      // rewrite updateQuantity  method
      cartItems.updateQuantity = async (line, quantity, name, source) => {
        console.log('Intercepted updateQuantity:', { line, quantity, name });
        
        if (source==='auto' || quantity ===0) {
          return originalUpdateQuantity(line, quantity, name);
        }

        try {
          await this.updateCartPriceBeforeNativeUpdate(line, quantity);
        } catch (error) {
          console.error('❌ Our price update failed:', error);
        }
        
        // execute native update
        console.log('🔄 Executing native cart update...');
        const result = await originalUpdateQuantity(line, quantity, name);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { line, quantity } }));
        
        return result;
      };
      
      // rewrite handleChange method
      cartItems.handleChange = (evt) => {
        // directly call the rewritten updateQuantity
        const source = 'manual';
        cartItems.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name, source);
      };
      
      // rewrite handleClick method
      cartItems.handleClick = (evt) => {
        if (!evt.target.matches('.js-remove-item')) return;
        evt.preventDefault();
        cartItems.updateQuantity(evt.target.dataset.index, 0, 'remove');
      };
    };
    
    waitForCartItems();
  }


  async updateCartPriceBeforeNativeUpdate(line, quantity) {
    console.log('🔄 Updating prices before native cart update:', { line, quantity });
    if(quantity===0) return;
    // if API request is already running, wait for it to complete
    if (this.getCartProductPriceListRunning) {
      console.log('⏳ Price update already running, waiting...');
      while (this.getCartProductPriceListRunning) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    try {
      await this.getCartProductPriceList('manual');
      console.log('✅ Price update completed successfully');
    } catch (error) {
      console.error('❌ Price update failed:', error);
      throw error;
    }
  }

  /**
   * Updates PDP page prices.
   */
  updatePDPPrice() {
    const priceElements = document.querySelectorAll("[shophyve-variant-id]");
    if (priceElements.length > 0) {
      const input = document.querySelector('.qty-input__input');
      const quantity = input ? input.value : 1;
      this.changeItemsPrice(quantity, priceElements);
    }
  }

  /**
   * Updates PLP page prices.
   */
  updatePLPPrice() {
    const priceElements = document.querySelectorAll("[shophyve-variant-id]");
    if (priceElements.length > 0) {
      this.changeItemsPrice(1, priceElements);
    }
  }


 
  showNotification(message) {
    const notification = document.createElement("div");
    notification.className = "a1-notification";
    notification.textContent = message;
    Object.assign(notification.style, {
      position: "fixed",
      top: "20px",
      right: "20px",
      backgroundColor: "#ff4d4f",
      color: "#fff",
      padding: "10px 20px",
      borderRadius: "5px",
      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
      zIndex: 10001,
      fontSize: "14px",
      animation: "fade-in-out 3s forwards",
    });
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  /**
   * Updates the prices of products displayed.
   * @param {Array} priceElements The elements containing product prices.
   * @param {Object} data The data containing new prices.
   * @param {string} idAttribute The attribute for identifying products.
   * @param {string} priceSelector The selector for price elements.
   * @param {string} currency The currency symbol.
   */
  updateProductPrices(
    priceElements,
    data,
    idAttribute,
    priceSelector,
    currency = this.getCookieValue("cart_currency") || "",
    listPriceSelector
  ) {
    const priceElementsArray = this.normalizeToArray(priceElements);
    priceElementsArray.forEach((priceContainer) => {
      const priceData = data?.data?.products?.find(
        (e) =>
          e?.ecommerce_product_id?.toString() ===
          priceContainer.getAttribute(`${idAttribute}`)?.toString()
      );
      if (priceData) {
        const newPrice = Number(priceData.net_price || 0).toFixed(2);
        const listPrice = Number(priceData.list_price || 0).toFixed(2);
        const priceEles = priceContainer.querySelectorAll(priceSelector);
        priceEles.forEach((item) => {
          item.textContent = `$${newPrice}`;
        });
        const listPriceEles = priceContainer.querySelectorAll(listPriceSelector);
        listPriceEles.forEach((item) => {
          item.textContent = `$${listPrice}`;
        });
        const warehouseContainer = document.getElementById(`warehouse-${priceData.ecommerce_product_id}`);
        // update warehouse-table
        this.updateWarehouseTable(warehouseContainer, priceData);
      }
      if (!priceData?.is_success) {
        const failedReason = priceData?.failed_reason || "data not found";
        const priceEles = priceContainer.querySelectorAll(priceSelector);
        priceEles.forEach((item) => {
          item.innerHTML = `${item.textContent?.replace(
            /\([^)]*\)/g,
            ""
          )}`;
        });
        const warehouseContainer = document.getElementById(`warehouse-${priceContainer.getAttribute(`${idAttribute}`)?.toString()}`);
        if(warehouseContainer) this.updateWarehouseTable(warehouseContainer, priceData);
      }
      this.clearLoadingState(priceContainer);
    });
  }
  
  clearLoadingState(priceContainer) {
    const loadingSpinner = priceContainer.querySelector(".shophyve-loading-spinner");
    if (loadingSpinner) {
      loadingSpinner.classList.add('hidden');
    }
  }

  /**
   * update warehouse-table inventory data
   * @param {HTMLElement} warehouseContainer - warehouse container element
   * @param {Object} priceData - price data, contains inventory information
   */
   updateWarehouseTable(warehouseContainer, priceData) {
    const warehouseTable = warehouseContainer
    if (!warehouseTable) {
      console.log('Warehouse table not found for inventory update');
      return;
    }
    warehouseContainer.innerHTML = '<div class="loader">Loading inventory...</div>';

    if(!priceData || !priceData.inventory){
      warehouseContainer.innerHTML = `
      <div class="inventory-available-container">
          <div class="inventory-available-card">
            <div class="inventory-backorder-info">
              <div class="inventory-backorder-message">Failed to Load Inventory</div>
            </div>
          </div>
        </div>`;
      return;
    }

    const responseInventory = Array.isArray(priceData?.inventory)? priceData?.inventory: [priceData?.inventory];
    const inventory = responseInventory?.filter((e)=>e?.LocationID?.toString() ==='11' || e?.LocationID?.toString() === '31')
    const totalQty = inventory?.reduce((acc, curr) => acc + Number(curr.FreeQuantity || '0'), 0);
    let html = `
      <div class="inventory-available-container">`;
    if(totalQty > 0) {
      html+=`<div class="inventory-available-header">Available Inventory:</div>
              <div class="inventory-available-locations">`
      for (let loc of inventory) {
        let LocationName = ''
        switch (loc.LocationID?.toString()) {
          case '11':
            LocationName = 'Moody'
            break;
          case '31':
            LocationName = 'Las Vegas'
            break;
          default:
            break;
        }
        html += ` <div class="inventory-each-location">
                    <div class="inventory-each-location-box">${LocationName || loc.LocationID || 'Unknown'}:</div><div class="inventory-location-quantity">${Number(loc.FreeQuantity || '0').toFixed(0)}</div>
                  </div>`;
      }
      html+=`</div>`;
      // clear productBackorder
      const productBackorder = warehouseContainer.parentElement.querySelector('.product-backorder');
      if(productBackorder){
        productBackorder.querySelector('.backorder')?.classList.add('hidden');
      }
    }
    else{
      html += ` <div class="inventory-available-card">
                  <div class="inventory-backorder-info">
                    <div class="inventory-backorder-message">Available for Backorder</div>
                  </div>
                  <div class="inventory-contact"><a href="tel:+18668372550">Contact</a> us for estimated delivery date.</div>
                </div>
                  `;
    }

    html += `</div>`;
    warehouseContainer.innerHTML = html;
  }

  async updateCartWarehouseTable(cartData) {
    let cartItems = [];
    if(!cartData){
      console.log('no cartData')
      const warehouseContainer = document.querySelectorAll('.stokes_warehouse_outer');
      for(let warehouseContainerElement of warehouseContainer){
        const variantId = warehouseContainerElement.getAttribute('data-variant-id')?.toString();
        const quantity = warehouseContainerElement.getAttribute('quantity')
        cartItems.push({
          variant_id: variantId,
          quantity: quantity,
        });
      }
    }else{
      cartItems = cartData.items;
    }
    const cartItemElements = document.querySelectorAll('.cart-item');
    for(let cartItemElement of cartItemElements) {
      const warehouseTable = cartItemElement.querySelector('.stokes_warehouse_outer');
      const variantId = cartItemElement.getAttribute('shophyve-variant-id')?.toString();
      const cartItemData = cartItems.find(item => item.variant_id?.toString() === variantId);
      if(warehouseTable) {
        const priceData = await this.cache.get(`${this.getCustomerId()}-${variantId}-${cartItemData.quantity}`);
        this.updateWarehouseTable(warehouseTable, priceData);
      }
    }
  }

  async addedToCart(product) {
    console.log('addedToCart product', product)
    if(!this.isB2BUser) return;
    if(!product?.variantId) return;
    const fetchData = await this.fetchAndCachePrices({
      products: [{
        ecommerce_product_id: product.variantId.toString(),
        quantity: product.quantity,
        item_id: product.sku,
      }],
      cart_id: this.getCookieValue("cart"),
      ecommerce_customer_id: this.getCustomerId(),
      ecommerce_company_id: this.getCompanyId(),
      company_location_id: this.getCompanyLocationId(),
    }, this.getCustomerId());
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { line: null, quantity: null } }));
    return fetchData;
  }

  /**
   * Initializes the socket.io connection.
   */
  initSocket() {
    if (!this.config.enableWebSocket) return;
    
    if (
      (!this.socket || this.socket.readyState === WebSocket.CLOSED) &&
      this.isB2BUser
    ) {
      this.socket = io(`${this.apiUrl}/api/v1/socket`, {
        transports: ["websocket"],
        path: "/api/v1/socket",
      });
      this.socket.on("connect", () => {
        console.log("connect success: " + this.socket.id);
      });
      this.socket.on("real-time-pricing", async (data) => {
        if (data?.had_price_changed) {
          this.showNotification(
            "The product price has changed, the latest price will be updated soon",
            "tip"
          );
          await this.sleep(3000);
          await this.startPriceUpdates();
        }
      });
      this.socket.on("disconnect", () => {
        console.log("WebSocket 连接断开，切换到轮询模式");
        if (this.config.enablePolling) {
          this.startPolling();
        }
      });
      
      this.socket.on("connect_error", (error) => {
        console.error("WebSocket 连接错误:", error);
        if (this.config.enablePolling) {
          this.startPolling();
        }
      });
    }
  }

  async getCachedProductPrices(products, customer_id=this.getCustomerId()) {
    const result = [];
    const miss = [];
    
    if (this.config.forceRefresh) {
      return { hit: [], miss: products };
    }
    const productList = Array.from(products);
    const cachedEntries = await Promise.all(
      productList.map((product) =>
        this.cache.get(`${customer_id}-${product.ecommerce_product_id}-${product.quantity}`)
      )
    );
    for (const [index, product] of productList.entries()) {
      const cached = cachedEntries[index];
      const now = Date.now();
      const isExpired = cached ? (now - cached.timestamp) > this.cache.maxAgeMs : true;
      if (cached && !isExpired) {
        result.push({ ...cached, is_success: true });
      } else {
        miss.push(product);
      }
    }
    return { hit: result, miss };
  }

  async sleep(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }
  sendSocketMessage(body) {
    if (this.need_websocket && this.socket) {
      const message = {
        a1_secret_key: this.shopHiveKey,
        body: {
          ...body,
          ecommerce_customer_id: this.getCustomerId(),
          ecommerce_company_id: this.getCompanyId(),
          company_location_id: this.getCompanyLocationId(),
        },
      };
      this.socket.emit("real-time-pricing", message);
    }
  }

  /**
   * Retrieves the value of a cookie by its key.
   * @param {string} key The key/name of the cookie.
   * @returns {string} The value of the cookie.
   */
  getCookieValue(key) {
    const name = `${key}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookiesArray = decodedCookie.split(";");

    for (let cookie of cookiesArray) {
      cookie = cookie.trim();
      if (cookie.startsWith(name)) {
        return cookie.substring(name.length);
      }
    }
    return "";
  }

  /**
   * Fetches data from the API.
   * @param {string} endpoint The API endpoint to call.
   * @param {Object} body The request body.
   * @param {Function} successCallback The callback function on successful request.
   * @param {Function} errorCallback The callback function on request failure.
   */
  async fetchData(endpoint, body) {
    try {

      const controller = new AbortController();
      const signal = controller.signal;

      const location = window.location.pathname;
      const basePath = '/' + location.split('/')[1];
      console.log(basePath)

      if(basePath === '/collections' || basePath === '/search'){
        if(window.previousAlgoliaRequest !== undefined) {
          window.previousAlgoliaRequest.abort();
        }
        window.previousAlgoliaRequest = controller;
      }
      const response = await fetch(`${this.apiUrl}${endpoint}`, {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          "a1-secret-key": this.shopHiveKey,
        },
        body: JSON.stringify(body),
        referrerPolicy: "no-referrer", // 不发送 referrer 信息
      });
      
      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result?.meta?.message || "unknown error";
        this.showNotification(errorMessage);
        clearInterval(this.intervalId);
        throw new Error(errorMessage);
      }
      return result;
    } catch (error) {
      console.error("Caught error:", error);
      
      if (error.name === 'AbortError') {
        const timeoutMessage = "Request timeout ";
        console.error(timeoutMessage);
        this.showNotification(timeoutMessage);
        throw new Error(timeoutMessage);
      }
      
      this.showNotification(error.message || "Unknown error");
      throw error;
    }
  }

  async fetchAndCachePrices(body, customer_id=this.getCustomerId()) {
    try {
      const data = await this.fetchData(
        "/api/v1/products/real-time-pricing",
        body
      );
      const products = data?.data?.products || [];

      for (const p of products) {
        if (p.is_success) {
          await this.cache.set(
            `${customer_id}-${p.ecommerce_product_id}-${p.quantity}`,
            {
              ...p,
              timestamp: Date.now(),
            }
          );
        }
      }

      return { products, fullResponse: data };
    } catch (error) {
      console.error('fetchAndCachePrices failed:', error);
      throw error;
    }
  }

  async fetchAndCachePricesAlgolia(
    body,
    customer_id = this.getCustomerId()
  ) {
    try {
      console.log("fetch and cache called");
      const data = await this.fetchData(
        "/api/v1/products/real-time-pricing",
        body
      );
      const products = data?.data?.products || [];

      for (const p of products) {
        if (p.is_success) {
          await this.cache.set(
            `${customer_id}-${p.ecommerce_product_id}-${p.quantity}`,
            {
              ...p,
              timestamp: Date.now(),
            }
          );
        }
      }

      return { products, fullResponse: data };
    } catch (error) {
      console.error("fetchAndCachePrices failed:", error);
      throw error;
    }
  }


  async getProductPrice(isIntervalRequest=false) {
    if (this.getProductPriceRunning) {
      console.log('get Product Price already running, skipping');
      return;
    }
    
    this.getProductPriceRunning = true;
    
    try {
      const priceElements = window.document.querySelectorAll("[shophyve-variant-id]");
      if (priceElements.length > 0) {
        await this.changeItemsPrice(1, priceElements,isIntervalRequest);
      }
      this.initPDPQuantityListeners();
    } finally {
      this.getProductPriceRunning = false;
    }
  }

  async startDrawerPriceUpdate(productEl,variantId,variantSku) { 
    const drawer = document.querySelector('quick-add-drawer');
    const input = drawer.querySelector(".qty-input__input");
    if (input) {
      input.addEventListener("change", async (e) => {
        const currentQuantity = Number(e.target.value || 1);
        const { hit, miss } = await this.getCachedProductPrices(
          [{
            ecommerce_product_id: variantId?.toString(),
            quantity: currentQuantity,
            item_id: variantSku,
          }],
          
        );
        let all = [...hit];
        const priceContentContainer = drawer.querySelector(".shophyve-price-element");
        const listPriceContainer = drawer.querySelector(".shophyve-list-price-element");
        if(miss.length > 0) {
          if (priceContentContainer) {
            let loadingSpinner = priceContentContainer.querySelector(".shophyve-loading-spinner");
            if (!loadingSpinner) {
              loadingSpinner = document.createElement("div");
              loadingSpinner.className = "shophyve-loading-spinner";
              priceContentContainer.appendChild(loadingSpinner);
            } else {
              loadingSpinner.classList.remove('hidden');
            }
          }
          const fetchResult = await this.fetchAndCachePrices({
            products: miss,
            ecommerce_customer_id: this.getCustomerId(),
            ecommerce_company_id: this.getCompanyId(),
            company_location_id: this.getCompanyLocationId(),
          }, this.getCustomerId());
          all = [...all, ...fetchResult.products];
        }
      
        const priceData = all[0];
        const newPrice = Number(priceData.net_price || 0).toFixed(2);
        const listPrice = Number(priceData.list_price || 0).toFixed(2);
        const currency = this.getCookieValue("cart_currency") || "";
        if(listPriceContainer){
          listPriceContainer.textContent = `$${listPrice}`;
        }
        if(priceContentContainer){
          priceContentContainer.textContent = `$${newPrice}`;
          this.clearLoadingState(priceContentContainer);
        }
      });
    }
    
    console.log('Drawer listeners initialized');
  }


  /**
   * Standardize various input types to an array
   * @param {*} input - maybe an array, NodeList, HTMLCollection, single element, string
   * @returns {Array} standardized array
   */
  normalizeToArray(input) {
    if (!input) {
      return [];
    }
    
    if (Array.isArray(input)) {
      return input;
    } else if (typeof input === 'string') {
      console.warn('Input is a string, treating as selector:', input);
      return Array.from(document.querySelectorAll(input));
    } else if (input.nodeType) {
      return [input];
    } else if (input.length !== undefined) {
      return Array.from(input);
    } else {
      console.warn('Input is not a valid type:', typeof input, input);
      return [];
    }
  }


  initPDPQuantityListeners() {
    if (this.pdpListenersInitialized) {
      return;
    }
    
    console.log('Initializing PDP quantity listeners...');
    
    const containers = [
      ...document.querySelectorAll(".product__info-wrapper"),
      ...document.querySelectorAll(".product-info"),
      ...document.querySelectorAll("[data-product-form]"),
      ...document.querySelectorAll("form[action*='/cart/add']"),
    ];
    
    
    containers.forEach((container, index) => {
      
      const input = container.querySelector(".qty-input__input");
      const priceElements = container.querySelectorAll("[shophyve-variant-id]");

      if (input && priceElements.length > 0) {
        input.addEventListener("change", async (e) => {
          const currentQuantity = e.target.value;
          console.log('Input value changed:', currentQuantity);
          if (currentQuantity && currentQuantity > 0) {
            await this.changeItemsPrice(currentQuantity, priceElements);
          }
        });
      }
      /*
      const minusButton = container.querySelector('.qty-input__btn[name="minus"]');
      const plusButton = container.querySelector('.qty-input__btn[name="plus"]');
      if (minusButton) {
        minusButton.addEventListener("click", async () => {
        if (priceElements.length > 0) {
          setTimeout(async () => {
            const currentQuantity = input.value;
            await this.changeItemsPrice(currentQuantity, priceElements);
          }, 10);
        }
        });
      }

      if (plusButton) {
        plusButton.addEventListener("click", async () => {
        if (priceElements.length > 0) {
          setTimeout(async () => {
            const currentQuantity = input.value;
            await this.changeItemsPrice(currentQuantity, priceElements);
          }, 10);
        }
        });
      } */
    });
    
    this.pdpListenersInitialized = true;
  }

  changeItemsPrice = async (quantity, priceElements, isIntervalRequest=false) => {
    const currency = this.getCookieValue("cart_currency") || "";

    const priceElementsArray = this.normalizeToArray(priceElements);
    
    if (priceElementsArray.length) {
      console.log('priceElementsArray',priceElementsArray.length)
      const products = new Map()
      for(const item of priceElementsArray) {
        const productId = item.getAttribute("shophyve-variant-id");
        const productSku = item.getAttribute("shophyve-variant-sku");

        const isValid = productSku !== null && productSku !== "";
        if(isValid) {
          products.set(productSku, {
            ecommerce_product_id: productId,
              item_id: productSku,
              quantity: quantity || 1,
          });
        }
      }
      
      const productsArray = Array.from(products.values());
      const customer_id = this.getCustomerId();
      const { hit, miss } = await this.getCachedProductPrices(
        productsArray,
        customer_id
      );

      const renderLoadingPriceElements = isIntervalRequest ? priceElementsArray?.map((item) => {
        const hitItem = hit?.find((e) => e.item_id === item.getAttribute("shophyve-variant-sku"));
        const price = item.querySelector(".shophyve-price-element")?.textContent?.replace(/[^\d.]/g, "") || '';
        if(price && hitItem?.net_price ){
          const currentPrice = parseFloat(price);
          const cachedPrice = parseFloat(hitItem.net_price);
          if(!isNaN(currentPrice) && !isNaN(cachedPrice) && Math.abs(currentPrice - cachedPrice) < 0.01){
            return null;
          }
        }
        return item;
      })?.filter(Boolean) || [] : priceElementsArray;
      
      renderLoadingPriceElements.forEach((item) => {
        let priceContentContainer = item.querySelector(".shophyve-price-element");
        if (priceContentContainer) {
          let loadingSpinner = priceContentContainer.querySelector(".shophyve-loading-spinner");
          if (!loadingSpinner) {
            loadingSpinner = document.createElement("div");
            loadingSpinner.className = "shophyve-loading-spinner";
            priceContentContainer.appendChild(loadingSpinner);
          } else {
            loadingSpinner.classList.remove('hidden');
          }
        }
        const warehouseContainer = document.getElementById(`warehouse-${item.getAttribute("shophyve-variant-id")}`);
        if(warehouseContainer) {
          warehouseContainer.innerHTML = '';
        }
      });
      
      const body = {
        products: miss?.map((e) => ({
          ecommerce_product_id: e?.ecommerce_product_id,
          quantity: Number(e?.quantity || 1),
          item_id : e?.item_id
        })),
        ecommerce_customer_id: this.getCustomerId(),
        ecommerce_company_id: this.getCompanyId(),
        company_location_id: this.getCompanyLocationId(),
      };
      
      let fetched = [];
      let all = [...hit]; 
      
      if (miss.length > 0) {
        try {
          const fetchResult = await this.fetchAndCachePrices(body, customer_id);
          fetched = fetchResult.products;
          all = [...fetched,...hit]; 
        } catch (error) {
          console.error('Failed to fetch product prices:', error);
        
        }
      }
      
      this.updateProductPrices(
        renderLoadingPriceElements,
        { data: { products: all } },
        "shophyve-variant-id",
        ".shophyve-price-element",
        currency,
        ".shophyve-list-price-element"
      );

      this.sendSocketMessage({
        ecommerce_customer_id: this.getCustomerId(),
        ecommerce_company_id: this.getCompanyId(),
        company_location_id: this.getCompanyLocationId(),
        need_origin_data: false,
        need_cart_info: true,
        products: body.products.map((item) => {
          const product = (all || []).find(
            (i) =>
              i.ecommerce_product_id === item.ecommerce_product_id &&
              i.is_success
          );
          return product
            ? {
                ...item,
                price: String(product.net_price),
              }
            : item;
        }),
      });
    }
  };

  /**
   * Retrieves and updates the prices of products in the cart.
   */
  async getCartProductPriceList(source='auto',locationId=this.getCurrentLocationId(),needShowLoading=false) {

    if (!this.isB2BUser) return;
    // prevent duplicate calls
    if (this.getCartProductPriceListRunning) {
      console.log('getCartProductPriceList already running, skipping');
      return;
    }
    
    this.getCartProductPriceListRunning = true;
    try {
    const priceElements = document.querySelectorAll("tr.cart-item");
    const customer_id = this.getCustomerId();
    if(source==='manual' || needShowLoading){
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.classList.add('pointer-events-none');
        
        priceElements.forEach((item) => {
          const cartLoader = item.querySelector(".cart-item__loader");
          if (cartLoader) {
            cartLoader.hidden = false;
          }
        });
      }
      
      const checkoutButton = document.querySelector('button[name="checkout"]');
      if (checkoutButton) {
        checkoutButton.disabled = true;
      }
    }

    if(source === 'auto'){
      this.addCartLoadingState(priceElements)
    }

    // Build the request body
    const products = [...priceElements]
      .map((item) => ({
        ecommerce_product_id: item.getAttribute("shophyve-variant-id"),
        item_id: item.getAttribute("shophyve-variant-sku"),
        quantity: Number(
          item.querySelector("input[name='updates[]']")?.value || 1
        ),
        price: String(item.querySelector(".shophyve-price-element")?.textContent?.replace(/[^\d.]/g, "") || '')
      }))
      .filter((product) => product.ecommerce_product_id !== null && product.ecommerce_product_id !== "");

    const body = {
      cart_id: this.getCookieValue("cart"),
      ecommerce_customer_id: customer_id,
      ecommerce_company_id: this.getCompanyId(),
      company_location_id: this.getCompanyLocationId(),
      location_id: locationId,
      products: products,
    };

    const { miss } = await this.getCachedProductPrices(
      products,
      customer_id,
      locationId
    );
    
    let fetched = [];
    
    let apiResponseData = null;
    
    if (miss.length > 0) {
      try {
        const fetchResult = await this.fetchAndCachePrices({
          ...body,
          products: miss
        }, customer_id, locationId);
        fetched = fetchResult.products;
        apiResponseData = fetchResult.fullResponse; 
        
        if (fetched.length === 0 && miss.length > 0) {
          console.warn('API request returned no data, but products were requested');
        }
      } catch (error) {
        console.error('Failed to fetch cart prices:', error);
      }
    } else {
      console.log('All products found in cache, no API request needed');
    }
     this.sendSocketMessage(body);
    if (source==='auto' && miss.length > 0) { //apiResponseData?.data?.need_refresh === true
      try {
        const quantityInput = document.querySelector('tr.cart-item input[name="updates[]"]');
        const cartItems = document.querySelector('cart-items');
        
        if (quantityInput) {
          console.log('start update updateQuantity')
          await cartItems.updateQuantity(
            quantityInput.dataset.index,
            quantityInput.value,
            'auto-refresh',
            'auto'
          );
        } else {
            console.warn('⚠️ No quantity input found, cannot trigger native refresh');
        }
      } catch (error) {
        console.error('❌ Failed to trigger native cart refresh:', error);
      } 
    }
    
    this.clearCartLoadingState(priceElements);
    } finally {
      this.getCartProductPriceListRunning = false;
    }
  }

 
  async updateProductRecommendationPrice(container) {
    if(!this.isB2BUser) return;

    const recommendationElements = container?.querySelectorAll("[shophyve-variant-id]");
    const recommendationPriceElementsArray = this.normalizeToArray(recommendationElements)
    if(!recommendationPriceElementsArray.length) return;
    const products = recommendationPriceElementsArray?.map((item) => {
      return {
        ecommerce_product_id: item.getAttribute("shophyve-variant-id"),
        item_id: item.getAttribute("shophyve-variant-sku"),
        quantity: 1,
        price: String(item.querySelector(".shophyve-price-element")?.textContent?.replace(/[^\d.]/g, "") || '')
      }
    });
    const { hit, miss } = await this.getCachedProductPrices(
      products,
      this.getCustomerId()
    );
    let all = [...hit]; 
    
    if (miss.length > 0) {
      const fetchResult = await this.fetchAndCachePrices({
        ecommerce_customer_id: this.getCustomerId(),
        ecommerce_company_id: this.getCompanyId(),
        company_location_id: this.getCompanyLocationId(),
        products: miss
      }, this.getCustomerId());
      all = [...hit, ...fetchResult.products]; 
    } 
    recommendationPriceElementsArray.forEach((item) => {
      let priceContentContainer = item.querySelector(".shophyve-price-element");
      
      if (priceContentContainer) {
        let loadingSpinner = priceContentContainer.querySelector(".shophyve-loading-spinner");
        if (!loadingSpinner) {
          loadingSpinner = document.createElement("div");
          loadingSpinner.className = "shophyve-loading-spinner";
          priceContentContainer.appendChild(loadingSpinner);
        } else {
          loadingSpinner.classList.remove('hidden');
        }
      }
    });
    const currency = this.getCookieValue("cart_currency") || "";
    this.updateProductPrices(
      recommendationPriceElementsArray,
      { data: { products: all } },
      "shophyve-variant-id",
      ".shophyve-price-element",
      currency,
      ".shophyve-list-price-element"
    );
  }
/**
 * 
 * Get Algolia inventory and pricing data from ERP through ShopHive API
 * @param {*} container 
 */
  async getAlgoliaInventoryAndPricing(container) {

    if (!this.isB2BUser || !container) return;

    const targetElements = container?.querySelectorAll("[data-variant-id]");

    if (!targetElements?.length) return;

    const products = Array.from(targetElements).map((item) => {
      const variantId = item.dataset.variantId;
      let sku = item.dataset.variantSku;

      return {
        ecommerce_product_id: variantId,
        item_id: sku,
        // Quantity is set to 1 as we want to get Price for each item ( / EA)
        quantity: 1,
      };
    });


    const { hit, miss } = await this.getCachedProductPrices(
      products,
      this.getCustomerId()
    );
    let all = [...hit];

    if (miss.length > 0) {
      const fetchResult = await this.fetchAndCachePricesAlgolia(
        {
          ecommerce_customer_id: this.getCustomerId(),
          ecommerce_company_id: this.getCompanyId(),
          company_location_id: this.getCompanyLocationId(),
          products: products,
        },
        this.getCustomerId()
      );
      all = [...hit, ...fetchResult.fullResponse.data.products];
    }

    const priceMap = {};
    all.forEach((p) => {
      if (p.ecommerce_product_id && p.is_success) {

        let totalQty = 0;

        const inventoryObj = p.inventory.reduce((acc, loc) => {
          const freeQty = parseFloat(loc.FreeQuantity) || 0;
          acc.push({
            LocationID: loc.LocationID,
            FreeQuantity: freeQty
          });
          if(['11', '31'].includes(loc.LocationID))
            totalQty += freeQty;
          return acc;
        }, []);

        priceMap[p.ecommerce_product_id] = {
          net_price: p.net_price,
          list_price: p.list_price,
          total_qty: totalQty,
          inventory : inventoryObj
        };
      }
    });

    return priceMap;
  }

  /* 
   * get cart selected warehouse
   * @param {Array} items - cart items: Array<{variant_id: number, quantity: number, sku: string}> | null
   * @returns {String} - selected warehouse: '11' | '31'
   */
  async getCartSelectedWarehouse(items) {
    let defaultLocation = '11'
    let cartItems = items
    if(!cartItems){
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        const cartData = await response.json()
        cartItems = cartData?.items || [];
      } catch (error) {
        console.log('get cartItems error', error)
        return defaultLocation;
      }
    }
 
    if(!cartItems.length) return defaultLocation;
    const products = cartItems?.map((item) => {
      return {
        ecommerce_product_id: item.variant_id?.toString(),
        item_id: item.sku,
        quantity: item.quantity,
      }
    });
    const { hit, miss } = await this.getCachedProductPrices(
      products,
      this.getCustomerId()
    );
    let all = [...hit];
    if(miss.length > 0) {
      const fetchResult = await this.fetchAndCachePrices({
        ecommerce_customer_id: this.getCustomerId(),
        ecommerce_company_id: this.getCompanyId(),
        company_location_id: this.getCompanyLocationId(),
        products: miss
      }, this.getCustomerId());
      all = [...all, ...fetchResult.products];
    }

    const filteredWithoutQtyIn_11 = all?.filter((item) => {
      const inventory = Array.isArray(item.inventory) ? item.inventory :[item.inventory]
      return inventory?.find((e)=> e.LocationID?.toString() === '11' && Number(e.FreeQuantity || 0) === 0);
    }) || [];
    const filteredWithoutQtyIn_31 = all?.filter((item) => {
      const inventory = Array.isArray(item.inventory) ? item.inventory :[item.inventory]
      return inventory?.find((e)=> e.LocationID?.toString() === '31' && Number(e.FreeQuantity || 0) === 0);
    }) || [];
    if(filteredWithoutQtyIn_11.length > 0 && !filteredWithoutQtyIn_31.length){
      defaultLocation = '31';
    }
    
    // Get select element
    const select = document.getElementById('cart_warehouse');
    select.value = defaultLocation;
    return defaultLocation;
  }

  clearCartLoadingState(priceElements) {
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      cartItems.classList.remove('pointer-events-none');
    }
    
    priceElements.forEach((item) => {
      const cartLoader = item.querySelector(".cart-item__loader");
      if (cartLoader) {
        cartLoader.hidden = true;
      }
    });
    
    const checkoutButton = document.querySelector('button[name="checkout"]');
    if (checkoutButton) {
      checkoutButton.disabled = false;
      checkoutButton.classList.remove('is-loading');
      
    }
  }
  
  async toCheckout() {
    const response = await fetch(`${theme.routes.cart}.js`);
    const cartData = await response.json()
    const cartItems = cartData?.items || [];
    if(cartItems?.find((item)=> parseInt(item?.price?.toString()) === 0)){
      const products = cartItems?.map((item) => {
        return {
          ecommerce_product_id: item.variant_id?.toString(),
          item_id: item.sku,
          quantity: item.quantity,
          price: item?.price?.toString()
        }
      });
      try {
        const fetchResult = await this.fetchAndCachePrices({
          ecommerce_customer_id: this.getCustomerId(),
          ecommerce_company_id: this.getCompanyId(),
          company_location_id: this.getCompanyLocationId(),
          products: products,
          cart_id: this.getCookieValue("cart"),
          need_update_cart: true,
        }, this.getCustomerId());
       
      } catch (error) {
        return false;
      }
    }
    return true;
  }

  addCartLoadingState(priceElements) {
     const cartItems = document.querySelector('cart-items');
     if (cartItems) {
       cartItems.classList.add('pointer-events-none');
     }
     
     priceElements.forEach((item) => {
       const cartLoader = item.querySelector(".cart-item__loader");
       if (cartLoader) {
         cartLoader.hidden = false;
       }
     });
     
    //  const checkoutButton = document.querySelector('button[name="checkout"]');
    //  if (checkoutButton) {
    //    checkoutButton.disabled = true;
    //    checkoutButton.classList.remove('is-loading');
       
    //  }
   }

 
  getCustomerId() {
    return localStorage.getItem("customer_id")
      ? "gid://shopify/Customer/" + localStorage.getItem("customer_id")
      : "";
  }

 
  getCompanyId() {
    return localStorage.getItem("company_id") || "";
  }

  getCompanyLocationId() {
    return localStorage.getItem("company_location_id") || "";
  }

  getIsB2BCustomer() {
    return localStorage.getItem("is_b2b_customer") && localStorage.getItem("is_b2b_customer") === "true";
  }

  getCurrentLocationId() {
    const select = document.getElementById('cart_warehouse');
    return select ? select.value || '11' : '11';
  }

  async updateCartPricesWithLocation(locationId) {
    console.log('Location changed to:', locationId);
    await this.getCartProductPriceList('auto',locationId,true);
  }
  
  getCustomerInfo() {
    return {
      customer_id: this.getCustomerId(),
      company_id: this.getCompanyId(),
      company_location_id: this.getCompanyLocationId(),
      is_b2b_customer: this.getIsB2BCustomer()
    };
  }

 


  isCartPage() {
    const isCartPath = window.location.pathname.includes('/cart');
    const hasCartFooter = document.querySelector('#main-cart-footer') !== null;
    const hasCartItems = document.querySelector('tr.cart-item') !== null;
    const hasCartDrawer = document.querySelector('cart-drawer') !== null;
    
    const isCart = isCartPath || hasCartFooter || hasCartItems || hasCartDrawer;
    return isCart;
  }
}

window.PriceUpdater = PriceUpdater;
window.initPriceUpdater = function(options = {}) {
  if (window.priceUpdater) {
    window.priceUpdater.stopPolling();
    if (window.priceUpdater.socket) {
      window.priceUpdater.socket.disconnect();
    }
  }
  
  window.priceUpdater = new PriceUpdater(null, null, options);
  return window.priceUpdater;
};



